import React, { useCallback, useMemo } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { CAMPAIGN_TABS, campaignTabOf, type CampaignTab } from '../outreachPaths';
import type { CampaignDetail, OutreachCampaignLive } from '../outreachTypes';
import { CampaignResultsTab } from '../results/CampaignResultsTab';
import { TodayTab } from './TodayTab';
import { PeopleList } from './PeopleList';
import { campaignPageMessages as messages } from './campaignPageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

const TAB_LABELS: Record<CampaignTab, MessageDescriptor> = {
  today: messages.tabToday,
  people: messages.tabPeople,
  results: messages.tabResults,
};

interface CampaignTabsProps {
  accountId: number;
  live: OutreachCampaignLive;
  campaign: CampaignDetail | undefined;
}

/** The tabs of a started campaign (design outreach-v2 n1): Today | People | Results, in `?tab=`. */
const CampaignTabsComponent: React.FC<CampaignTabsProps> = ({ accountId, live, campaign }) => {
  const intl = useIntl();
  const [params, setParams] = useSearchParams();
  const tab = campaignTabOf(params.get('tab'));
  const open = useCallback(
    (next: CampaignTab) => {
      setParams(
        (current) => {
          const out = new URLSearchParams(current);
          if (next === 'today') {
            out.delete('tab');
          } else {
            out.set('tab', next);
          }
          return out;
        },
        { replace: true }
      );
    },
    [setParams]
  );
  const buttons = useMemo(
    () =>
      CAMPAIGN_TABS.map((key) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={key === tab}
          className={clsx(shared.tab, key === tab && shared.tabOn)}
          onClick={() => open(key)}
          data-testid={`campaign-tab-${key}`}
        >
          {intl.formatMessage(TAB_LABELS[key])}
        </button>
      )),
    [intl, open, tab]
  );

  const body = () => {
    switch (tab) {
      case 'people':
        return (
          <PeopleList
            accountId={accountId}
            campaignUuid={live.campaignUuid}
            releaseDay={campaign?.releaseDate?.slice(0, 10) ?? null}
          />
        );
      case 'results':
        return <CampaignResultsTab accountId={accountId} campaignUuid={live.campaignUuid} />;
      case 'today':
      default:
        return <TodayTab accountId={accountId} live={live} campaign={campaign} />;
    }
  };

  return (
    <>
      <div className={shared.tabs} role="tablist" aria-label={intl.formatMessage(messages.tabsLabel)}>
        {buttons}
      </div>
      <div role="tabpanel">{body()}</div>
    </>
  );
};

export const CampaignTabs = TypedMemo(CampaignTabsComponent);
